import { Component, DestroyRef, OnDestroy, WritableSignal, inject, signal } from '@angular/core';
import { Location, NgClass, NgStyle } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, NavigationError, NavigationStart, Router, RouterEvent } from '@angular/router';

import { GlobalsService } from '@services/globals.service';
import { GoogleTranslateService } from '@services/google-translate.service';
import { GtmService } from '@services/gtm.service';
import { ModalService } from '@services/modal.service';
import { SeoService } from '@services/seo.service';
import { SsrService } from '@services/ssr.service';
import { RoutingData } from '@core/routing-data';

@Component({
  selector: 'app-page-loader',
  imports: [NgClass, NgStyle],
  templateUrl: './page-loader.component.html',
  styleUrls: ['./page-loader.component.scss'],
})
export class PageLoaderComponent implements OnDestroy {
  private location = inject(Location);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private globalsService = inject(GlobalsService);
  private googleTranslateService = inject(GoogleTranslateService);
  private gtmService = inject(GtmService);
  private modalService = inject(ModalService);
  private seoService = inject(SeoService);
  private ssrService = inject(SsrService);
  public width: WritableSignal<number> = signal(0);
  public done: WritableSignal<boolean> = signal(false);
  private previousUrl: string = '';
  private setTimeoutLoad: any;
  private setTimeoutDone: any;
  private setTimeoutFinal: any;
  private locationChanges?: VoidFunction;
  private translated: boolean = false;

  checkRouterEvent(routerEvent: RouterEvent): void {
    this.setTimeoutLoad = setTimeout(() => {
      if (routerEvent instanceof NavigationStart) {
        const url = routerEvent.url?.split('?')[0];
        if (this.previousUrl === url) return;
        this.previousUrl = url;

        if (this.setTimeoutDone) clearTimeout(this.setTimeoutDone);
        if (this.setTimeoutFinal) clearTimeout(this.setTimeoutFinal);
        this.width.set(10);
        this.done.set(false);
      } else if (routerEvent instanceof NavigationEnd || routerEvent instanceof NavigationError) {
        this.seoService.setCanonical(routerEvent.url);
        this.seoService.setRobotsIndex();
        setTimeout(() => {
          const excludedPageTypes = ['Product', 'Form', 'Purchase'];
          const pageType = this.gtmService.getPageType() ?? '';
          if (!excludedPageTypes.includes(pageType)) {
            this.gtmService.pushGtmLandingPage({});
          }
          const pageTypeReferrer = this.gtmService.getPageType() ?? RoutingData.pageTypeDefault;
          sessionStorage.setItem('pageTypeReferrer', pageTypeReferrer);
          sessionStorage.setItem('prevPage', `${window.location.href}`);
        }, 250);
        this.gtmService.loadUtm();
        const pageType = this.gtmService.getPageType();
        (window as any).pageType = pageType;
        this.width.set(100);

        const modal: string | null = this.activatedRoute.snapshot.queryParamMap.get('modal');
        if (modal) {
          if (modal === 'domluvit-schuzku') {
            this.modalService.open('<app-book-appointment-modal></app-book-appointment-modal>', 'medium');
          } else {
            this.modalService.open('<app-' + modal + '-modal></app-' + modal + '-modal>', 'small');
          }
        } else {
          this.modalService.close();
        }
        this.setTimeoutDone = setTimeout(() => {
          this.done.set(true);
          this.width.set(100);
          this.setTimeoutFinal = setTimeout(() => {
            this.width.set(0);
            if (this.ssrService.isBrowser() && !this.translated) {
              this.googleTranslateService.handleHashLanguageInit();
              this.translated = true;
            }
          }, 300);
        }, 300);
      }
    }, 1);
  }

  constructor() {
    this.width.set(10);
    this.done.set(false);
    if (this.ssrService.isServer()) return;
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((routerEvent: any) => {
      this.checkRouterEvent(routerEvent);
    });
    this.locationChanges = this.location.onUrlChange((url: string) => {
      this.globalsService.updateUrl(url);
    });
  }

  ngOnDestroy(): void {
    if (this.setTimeoutLoad) clearTimeout(this.setTimeoutLoad);
    if (this.setTimeoutDone) clearTimeout(this.setTimeoutDone);
    if (this.setTimeoutFinal) clearTimeout(this.setTimeoutFinal);
    if (this.locationChanges) this.locationChanges();
  }
}
